import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["notification", "uploadCount", "errorList", "statusImage", "errorTemplate"]
  static values = {
    loadingImage: String,
    successImage: String,
    errorImage: String
  }

  #errors = []
  #totalCount = 0
  #processedCount = 0
  #hideTimeout = null

  connect() {
    this.element.addEventListener("document-upload:progress", (e) => this.updateProgress(e))
    this.element.addEventListener("document-upload:error", (e) => this.showError(e))
    this.element.addEventListener("document-upload:complete", (e) => this.handleComplete(e))
  }

  disconnect() {
    this.#cancelAutoHide()
  }

  dismiss() {
    this.hide()
  }

  updateProgress(event) {
    const { processed, total } = event.detail
    this.#processedCount = processed
    this.#totalCount = total

    this.#show()

    if (this.hasUploadCountTarget) {
      this.uploadCountTarget.textContent = `Uploaded ${this.#processedCount} of ${this.#totalCount} documents`
    }

    this.#updateStatus()

    if (this.#isCompleteWithoutErrors()) {
      this.#scheduleAutoHide()
    }
  }

  showError(event) {
    const { filename, message } = event.detail
    this.#errors.push({ filename, message })
    this.#show()
    this.#renderErrors()
    this.#updateStatus()
  }

  handleComplete(event) {
    this.#updateStatus()
    if (this.#isCompleteWithoutErrors()) {
      this.#scheduleAutoHide()
    }
  }

  hide() {
    if (this.hasNotificationTarget) {
      this.notificationTarget.classList.add('hidden')
      this.notificationTarget.classList.remove('block')
    }
    this.#reset()
  }

  #show() {
    if (this.hasNotificationTarget) {
      this.notificationTarget.classList.remove('hidden')
      this.notificationTarget.classList.add('block')
    }
  }

  #updateStatus() {
    const isComplete = this.#processedCount >= this.#totalCount
    const hasErrors = this.#errors.length > 0

    if (this.hasStatusImageTarget) {
      let status = 'loading'
      if (isComplete) {
        status = hasErrors ? 'error' : 'success'
      }

      const imageUrl = this[`${status}ImageValue`]
      if (imageUrl) {
        this.statusImageTarget.src = imageUrl
      }
    }
  }

  #renderErrors() {
    if (!this.hasErrorListTarget || !this.hasErrorTemplateTarget) return

    this.#clearErrors()

    if (this.#errors.length > 0) {
      this.#errors.forEach(error => {
        const errorElement = this.errorTemplateTarget.content.cloneNode(true)
        const filenameSpan = errorElement.querySelector('[data-error-target="filename"]')
        const messageSpan = errorElement.querySelector('[data-error-target="message"]')

        if (filenameSpan) filenameSpan.textContent = `${error.filename}: `
        if (messageSpan) messageSpan.textContent = error.message

        this.errorListTarget.appendChild(errorElement)
      })
      this.errorListTarget.classList.remove('hidden')
    }
  }

  #clearErrors() {
    if (this.hasErrorListTarget) {
      this.errorListTarget.innerHTML = ''
      this.errorListTarget.classList.add('hidden')
    }
  }

  #isCompleteWithoutErrors() {
    return this.#processedCount >= this.#totalCount && this.#errors.length === 0
  }

  #scheduleAutoHide() {
    this.#cancelAutoHide()
    this.#hideTimeout = setTimeout(() => {
      this.hide()
    }, 3000)
  }

  #cancelAutoHide() {
    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout)
      this.#hideTimeout = null
    }
  }

  #reset() {
    this.#cancelAutoHide()
    this.#errors = []
    this.#totalCount = 0
    this.#processedCount = 0
    this.#clearErrors()
  }
}
